import { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, Copy, Check, ChevronDown, Eye, EyeOff, Lock } from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

const ApiKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyData, setNewKeyData] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealedKeyId, setRevealedKeyId] = useState(null);
  const [revealError, setRevealError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/admin/api-keys');
      setKeys(data);
    } catch { toast.error('Failed to load API keys'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const { data } = await apiClient.post('/admin/api-keys', { name: newKeyName.trim() });
      setNewKeyData(data);
      toast.success('API key created — save it now!');
      fetchKeys();
      setNewKeyName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? This action cannot be undone.')) return;
    try {
      await apiClient.delete(`/admin/api-keys/${keyId}`);
      toast.success('API key revoked');
      if (expandedKey === keyId) setExpandedKey(null);
      if (revealedKeyId === keyId) setRevealedKeyId(null);
      fetchKeys();
    } catch { toast.error('Failed to revoke key'); }
  };

  const handleRevealKey = async (keyId) => {
    if (!revealPassword) { setRevealError('Enter your console password'); return; }
    setVerifying(true);
    setRevealError('');
    try {
      // Verify password by attempting login
      const { data } = await apiClient.post(`/admin/api-keys/${keyId}/reveal`, { password: revealPassword });
      if (data.key) {
        setRevealedKeyId(keyId);
        // Store the revealed key temporarily
        setKeys(prev => prev.map(k => k.id === keyId ? { ...k, full_key: data.key } : k));
        toast.success('Key revealed');
        setRevealPassword('');
      }
    } catch (err) {
      setRevealError(err.response?.data?.detail || 'Invalid password');
    } finally { setVerifying(false); }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div data-testid="api-keys-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-sm font-bold text-[#0F1B2D] uppercase tracking-wider">API Keys</h3>
          <p className="text-xs text-[#9B9589] mt-1">Manage API keys for Slate, Lobby, and external integrations</p>
        </div>
        <button onClick={() => { setShowCreateForm(!showCreateForm); setNewKeyData(null); }}
          className="btn-ainaa-primary px-4 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-2" data-testid="create-key-btn">
          <Plus size={14} />New Key
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="border-2 border-[#0F1B2D] bg-white p-6 mb-6" data-testid="create-key-form">
          <form onSubmit={handleCreate} className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold block mb-2">Key Name</label>
              <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} className="input-ainaa h-11" placeholder="e.g., Slate Production Key" required data-testid="key-name-input" />
            </div>
            <button type="submit" disabled={creating} className="btn-ainaa-primary px-6 py-2.5 text-xs font-semibold uppercase tracking-wider" data-testid="submit-key-btn">
              {creating ? 'Creating...' : 'Generate'}
            </button>
          </form>

          {newKeyData && (
            <div className="mt-5 bg-emerald-50 border-2 border-emerald-500 p-5" data-testid="new-key-display">
              <div className="flex items-center gap-2 mb-3">
                <Key size={16} className="text-emerald-600" />
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Save This Key Now — It Won't Be Shown Again</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white border border-emerald-300 px-4 py-3 text-sm text-[#0F1B2D] break-all select-all" data-testid="new-key-value">
                  {newKeyData.key}
                </code>
                <button onClick={() => copyToClipboard(newKeyData.key, 'new')}
                  className="flex-shrink-0 p-2.5 border border-emerald-300 hover:bg-emerald-100 transition-colors" data-testid="copy-new-key">
                  {copiedId === 'new' ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} className="text-emerald-600" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Key List */}
      <div className="border border-[#D8D3CB] bg-white divide-y divide-[#D8D3CB]/50" data-testid="key-list">
        {loading ? (
          <div className="p-12 text-center text-[#9B9589]">Loading...</div>
        ) : keys.length === 0 ? (
          <div className="p-12 text-center"><Key className="w-10 h-10 text-[#D8D3CB] mx-auto mb-3" /><p className="text-[#9B9589] font-medium text-sm">No API keys created yet</p></div>
        ) : keys.map((k) => (
          <div key={k.id}>
            <div
              className={`px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-[#EDECEA]/30 ${expandedKey === k.id ? 'bg-[#F8F7F5]' : ''}`}
              onClick={() => { setExpandedKey(expandedKey === k.id ? null : k.id); setRevealPassword(''); setRevealError(''); }}
              data-testid={`key-row-${k.id}`}
            >
              <div className="w-9 h-9 bg-[#0F1B2D] flex items-center justify-center flex-shrink-0">
                <Key size={14} className="text-[#C4A35A]" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-bold text-[#0F1B2D] hover:text-[#C4A35A] transition-colors" data-testid={`key-name-${k.id}`}>{k.name}</span>
                <p className="text-[10px] text-[#9B9589] mt-0.5">
                  Created {k.created_at ? new Date(k.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'recently'}
                  {k.last_used && <> &middot; Last used {new Date(k.last_used).toLocaleDateString()}</>}
                </p>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 ${k.status === 'active' ? 'text-emerald-700 bg-emerald-50 border-2 border-emerald-300' : 'text-red-700 bg-red-100 border-2 border-red-400'}`}>
                {k.status || 'active'}
              </span>
              <ChevronDown size={14} className={`text-[#9B9589] transition-transform ${expandedKey === k.id ? 'rotate-180' : ''}`} />
            </div>

            {expandedKey === k.id && (
              <div className="px-5 pb-5 pt-1">
                <div className={`p-5 ${k.status === 'revoked' ? 'bg-red-50 border border-red-300' : 'bg-emerald-50 border border-emerald-200'}`} data-testid={`key-detail-${k.id}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <Eye size={14} className={k.status === 'revoked' ? 'text-red-600' : 'text-emerald-600'} />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${k.status === 'revoked' ? 'text-red-700' : 'text-emerald-700'}`}>Key Details — {k.name}</span>
                  </div>

                  {/* Key display */}
                  <div className="mb-4">
                    <label className={`text-[9px] font-bold uppercase block mb-1.5 ${k.status === 'revoked' ? 'text-red-600' : 'text-emerald-600'}`}>API Key</label>
                    {revealedKeyId === k.id && k.full_key ? (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white border-2 border-emerald-400 px-4 py-3 text-sm text-[#0F1B2D] break-all select-all" data-testid={`revealed-key-${k.id}`}>
                          {k.full_key}
                        </code>
                        <button onClick={(e) => { e.stopPropagation(); copyToClipboard(k.full_key, k.id); }}
                          className="flex-shrink-0 p-2.5 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors" data-testid={`copy-full-key-${k.id}`}>
                          {copiedId === k.id ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <code className="flex-1 bg-white border border-emerald-200 px-4 py-3 text-sm text-[#9B9589]">
                            {k.prefix || k.key_prefix || '****'}{'•'.repeat(32)}
                          </code>
                        </div>
                        <div className="flex items-center gap-2">
                          <Lock size={14} className="text-emerald-600 flex-shrink-0" />
                          <Input
                            type="password"
                            value={revealPassword}
                            onChange={(e) => { setRevealPassword(e.target.value); setRevealError(''); }}
                            placeholder="Enter console password to reveal"
                            className="h-9 text-xs flex-1 border-emerald-200 focus:border-emerald-500"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleRevealKey(k.id); } }}
                            data-testid={`reveal-password-${k.id}`}
                          />
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRevealKey(k.id); }}
                            disabled={verifying}
                            className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-1"
                            data-testid={`reveal-btn-${k.id}`}
                          >
                            {verifying ? 'Verifying...' : <><EyeOff size={12} />Reveal</>}
                          </button>
                        </div>
                        {revealError && <p className="text-[10px] text-red-500 font-medium mt-1">{revealError}</p>}
                      </div>
                    )}
                  </div>

                  <div className={`flex items-center gap-2 pt-3 border-t ${k.status === 'revoked' ? 'border-red-200' : 'border-emerald-200'}`}>
                    <span className={`text-[9px] font-bold uppercase ${k.status === 'revoked' ? 'text-red-600' : 'text-emerald-600'}`}>Prefix:</span>
                    <code className="text-xs text-[#0F1B2D]">{k.prefix || k.key_prefix || '—'}</code>
                    <div className="ml-auto">
                      <button onClick={(e) => { e.stopPropagation(); handleRevoke(k.id); }}
                        className="px-3 py-1.5 text-[10px] font-bold uppercase text-red-500 border border-red-200 hover:bg-red-50 transition-colors flex items-center gap-1"
                        data-testid={`revoke-key-${k.id}`}>
                        <Trash2 size={12} />Revoke
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApiKeysPanel;
